// @ts-nocheck
import { ColumnDataType, Columns, GetColumnTextProps } from './AutoSizeGrid.types'

export function getColumnText({ columnValue }: GetColumnTextProps) {
  if (typeof columnValue === 'boolean') {
    return columnValue ? 'Yes' : 'No'
  }

  return columnValue.toString()
}

export function getColumnValue<DataType>({
  columnKey,
  rowData,
}: Pick<ColumnDataType<DataType>, 'columnKey' | 'rowData'>) {
  const keys = columnKey.split('.')

  if (keys.length === 1) {
    const columnValue = rowData[keys[0]] || ''
    return getColumnText({ columnValue })
  }

  const currentData =
    keys.reduce(
      (accValue, currentValue) => accValue?.[currentValue] || rowData[currentValue],
      '',
    ) || ''

  return getColumnText({ columnValue: currentData })
}

interface GetDynamicColumnWidthProps<RowDataType> {
  columnCount: number
  columns: Columns<RowDataType>[]
  fallbackWidth: number
  resizedColumnsWidths: Record<number, number>
  scrollBarWidth: number
  totalRowWidth: number
}

export function getDynamicColumnWidth<RowDataType>({
  columnCount,
  columns,
  fallbackWidth,
  resizedColumnsWidths,
  scrollBarWidth,
  totalRowWidth,
}: GetDynamicColumnWidthProps<RowDataType>) {
  const totalDefinedWidthColumns = columns.reduce(
    (accTotals, currentCol, index) => {
      /**
       * Get the current value for the column width
       * First we validate if the column has a resized width
       * If not we get the column's original width or default to 0
       */
      const currentColumnWidth = resizedColumnsWidths[index] || currentCol.width || 0
      /**
       * If a column has a valid width, we will calculate it
       * This is so we can exclude it when we attempt to get the dynamic width size
       */
      const currentNumberOfColumnsWithWidthDefined =
        currentColumnWidth !== 0
          ? accTotals.numberOfColumnsWithWidthDefined + 1
          : accTotals.numberOfColumnsWithWidthDefined

      return {
        totalWidth: accTotals.totalWidth + currentColumnWidth,
        numberOfColumnsWithWidthDefined: currentNumberOfColumnsWithWidthDefined,
      }
    },
    {
      totalWidth: 0,
      numberOfColumnsWithWidthDefined: 0,
    },
  )

  const widthAvailableForDynamicColumnWidths =
    totalRowWidth - totalDefinedWidthColumns.totalWidth - scrollBarWidth

  /**
   * If we have width available we will calculate for each dynamic column width
   * if we don't we use the fallbackWidth value
   */
  if (widthAvailableForDynamicColumnWidths > 0) {
    // We based the width for dynamic widths for only columns that will use them
    const numberOfColumnsWithDynamicWidths =
      columnCount - totalDefinedWidthColumns.numberOfColumnsWithWidthDefined
    const dynamicColumnWidth =
      widthAvailableForDynamicColumnWidths / numberOfColumnsWithDynamicWidths

    return dynamicColumnWidth
  }

  return fallbackWidth
}
